export const validHeroScreenIds = ['code', 'plan', 'collaborate', 'automate', 'secure'] as const
export type HeroScreenId = (typeof validHeroScreenIds)[number]

interface HeroVisual {
  type: 'image' | 'video'
  url: {
    mobile?: string
    desktop: string
  }
  duration: number // seconds
  zIndex?: number
  ariaHidden?: boolean
  alt: string
}

interface HeroScreen {
  id: HeroScreenId
  visuals: HeroVisual[]
}

// TODO: Update mobile assets once known
export const SCREENS: HeroScreen[] = [
  {
    id: 'code',
    visuals: [
      {
        type: 'video',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/code-1_mobile.mp4',
          desktop: '/images/modules/site/home24/intro/carousel/code-1_desktop.mp4',
        },
        duration: 15,
        alt: 'A demonstration animation of a code editor using GitHub Copilot Chat, where the user requests GitHub Copilot to refactor duplicated logic and extract it into a reusable function for a given code snippet.',
      },
    ],
  },
  {
    id: 'plan',
    visuals: [
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/plan-1_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/plan-1_desktop.webp',
        },
        duration: 1,
        alt: 'A project management dashboard showing tasks organized by columns with progress categories like ‘Planning,’ ‘Building,’ ‘Behind,’ and ‘Complete.’',
      },
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/plan-2_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/plan-2_desktop.webp',
        },
        duration: 1,
        alt: 'A project management dashboard showing tasks organized by milestone for the ‘OctoArcade Invaders’ project, with tasks grouped under categories like ‘Engine,’ ‘Game Loop,’ and ‘Art’ in a table layout.',
      },
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/plan-3_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/plan-3_desktop.webp',
        },
        duration: 3,
        alt: 'A project management dashboard showing tasks organized by squads in the column on the left and a timeline view of tasks on the right.',
      },
    ],
  },
  {
    id: 'collaborate',
    visuals: [
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/collaborate-1_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/collaborate-1_desktop.webp',
        },
        duration: 1,
        alt: 'A pull request discussion displaying a series of comments and tasks related to improving alien character controls, including updates from multiple team members.',
      },
      {
        type: 'image',
        url: {
          desktop: '/images/modules/site/home24/intro/carousel/collaborate-2_desktop.webp',
        },
        duration: 2,
        alt: 'A code review comment displaying highlighted code and the code diff for a suggested change.',
      },
    ],
  },
  {
    id: 'automate',
    visuals: [
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/automate-1_mobile.webp?v=1',
          desktop: '/images/modules/site/home24/intro/carousel/automate-1_desktop.webp?v=1',
        },
        zIndex: 3,
        duration: 1,
        alt: 'GitHub Actions workflow titled ‘matrix-build-deploy.yml’. The Build stage has completed successfully in 1 minute and 42 seconds.',
      },
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/automate-2_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/automate-2_desktop.webp',
        },
        zIndex: 2,
        duration: 1,
        alt: 'The Test stage includes builds for Linux, macOS, and Windows, all of which have also completed successfully with their respective durations.',
      },
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/automate-3_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/automate-3_desktop.webp',
        },
        zIndex: 1,
        duration: 1,
        alt: 'The final stage, Publish, shows that the publishing steps for Linux, macOS, and Windows are pending and waiting for approval.',
      },
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/automate-0_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/automate-0_desktop.webp',
        },
        zIndex: 0,
        ariaHidden: true,
        duration: 0,
        alt: '',
      },
    ],
  },
  {
    id: 'secure',
    visuals: [
      {
        type: 'image',
        url: {
          mobile: '/images/modules/site/home24/intro/carousel/secure-1_mobile.webp',
          desktop: '/images/modules/site/home24/intro/carousel/secure-1_desktop.webp',
        },
        duration: 1,
        alt: 'Trend graph showing a decline in critical vulnerabilities over time.',
      },
      {
        type: 'image',
        url: {
          desktop: '/images/modules/site/home24/intro/carousel/secure-2_desktop.webp',
        },
        duration: 2,
        alt: 'Copilot Autofix identifies vulnerable code and provides an explanation, together with a secure code suggestion to remediate the vulnerability.',
      },
    ],
  },
]

export const getScreen = (screenId: HeroScreenId) => SCREENS.find(screen => screen.id === screenId)
